import type { TooltipProps } from "@mui/material";
import {
  LinearProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  tooltipClasses,
  Typography,
} from "@mui/material";
import {
  AttachMoney as MarkAsPaidIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Paid as PaidIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import dayjs, { DATE_FORMAT } from "../../dayjs";
import type { FC, ReactElement } from "react";
import type { PaymentBundleResponse } from "./paymentsApi";
import { downloadPaymentBundleXml } from "./paymentsApi";
import DownloadFileButton from "../../components/downloadFileButton/DownloadFileButton";
import { useSnackbar } from "notistack";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import LabelledCheckbox from "../../components/labelledCheckbox/LabelledCheckbox";
import { styled } from "@mui/material/styles";

interface DownloadXmlButtonProps {
  readonly bundleId: string;
  readonly bundleName: string;
}

const DownloadXmlButton: FC<DownloadXmlButtonProps> = ({ bundleId, bundleName }) => {
  const { enqueueSnackbar } = useSnackbar();
  return (
    <DownloadFileButton
      buttonText="Pobierz"
      startIcon={<DownloadIcon />}
      color="primary"
      options={[
        {
          name: "Pobierz XML",
          apiDefinition: () => downloadPaymentBundleXml(bundleId),
        },
      ]}
      onError={(e) =>
        enqueueSnackbar(`Nie udało się pobrać paczki płatności. Powód: ${(e as Error).message}`, { variant: "error" })
      }
      filename={`${bundleName}.xml`}
      variant="text"
    />
  );
};

interface ButtonProps {
  onClick: () => Promise<void>;
}

const DeleteBundleButton: FC<ButtonProps> = ({ onClick }) => (
  <AsyncLoadingButton startIcon={<DeleteIcon />} variant="text" color="error" onClick={onClick}>
    Usuń
  </AsyncLoadingButton>
);

const MarkBundleAsPaidButton: FC<ButtonProps> = ({ onClick }) => (
  <AsyncLoadingButton startIcon={<MarkAsPaidIcon />} variant="text" color="secondary" onClick={onClick}>
    Oznacz jako opłacona
  </AsyncLoadingButton>
);

const NoMaxWidthTooltip = styled(({ className, children, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} leaveDelay={200}>
    {children}
  </Tooltip>
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
    backgroundColor: "white",
    boxShadow:
      "0px 11px 15px -7px rgba(0, 0, 0, 0.2), 0px 24px 38px 3px rgba(0, 0, 0, 0.14), 0px 9px 46px 8px rgba(0, 0, 0, 0.12)",
  },
});

interface InvoicesTooltipProps {
  readonly invoices: { readonly invoiceNumber: string; costIds: string[] }[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  readonly children: ReactElement<unknown, any>;
}

const InvoicesTooltip: FC<InvoicesTooltipProps> = ({ invoices, children }) => (
  <NoMaxWidthTooltip
    title={
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Numer faktury</TableCell>
            <TableCell>Koszty</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.map((i) => (
            <TableRow key={i.invoiceNumber}>
              <TableCell>{i.invoiceNumber}</TableCell>
              <TableCell>{i.costIds.join(", ")}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    }
  >
    {children}
  </NoMaxWidthTooltip>
);

interface Props {
  readonly values: PaymentBundleResponse[];
  readonly isLoading: boolean;
  readonly showPaid: boolean;
  onShowPaidChange: (showPaid: boolean) => void;
  onDelete: (bundleId: string) => Promise<void>;
  onMarkAsPaid: (bundleId: string) => Promise<void>;
}

const PaymentBundles: FC<Props> = ({ values, isLoading, showPaid, onShowPaidChange, onMarkAsPaid, onDelete }) => (
  <>
    <Stack direction="row" spacing={1}>
      <Typography variant="h6">Paczki przelewów</Typography>
      <LabelledCheckbox label="Pokaż opłacone" checked={showPaid} onChange={onShowPaidChange} />
    </Stack>
    {isLoading && <LinearProgress />}
    <Table>
      <TableHead>
        <TableRow>
          <TableCell>Nazwa paczki</TableCell>
          <TableCell>Data utworzenia</TableCell>
          <TableCell>Faktury</TableCell>
          <TableCell />
          <TableCell />
          <TableCell />
        </TableRow>
      </TableHead>
      <TableBody>
        {values.map((b) => (
          <TableRow key={b.id} data-testid="payment-bundle-row">
            <TableCell>{b.name}</TableCell>
            <TableCell>{dayjs(b.createdAt).format(DATE_FORMAT)}</TableCell>
            <TableCell>
              <InvoicesTooltip invoices={b.invoices}>
                <ReceiptIcon />
              </InvoicesTooltip>
            </TableCell>
            <TableCell>
              <DownloadXmlButton bundleId={b.id} bundleName={b.name} />
            </TableCell>
            <TableCell>
              {b.status === "PAID" ? (
                <Typography>
                  <PaidIcon
                    color="success"
                    style={{
                      paddingRight: 5,
                      verticalAlign: "bottom",
                    }}
                  />
                  Opłacona
                </Typography>
              ) : (
                <MarkBundleAsPaidButton onClick={() => onMarkAsPaid(b.id)} />
              )}
            </TableCell>
            <TableCell>
              <DeleteBundleButton onClick={() => onDelete(b.id)} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </>
);

export default PaymentBundles;
